import { format, isValid } from 'date-fns';

import { TierName, TierStatusName } from '@order/graphql';

import type { AccountSweetpassMembershipSection } from '../../useSweetpassAccountContentfulData';
import type {
  AsyncCta,
  GetSectionProps,
  SweetpassSectionProps,
} from '../types';

export const getMembershipLevelSection = (
  props: GetMembershipLevelSectionProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const { tier, tierStatus, hasPaidTierHistory, joinDate, activeSubscription } =
    props;

  const profileJoinDate = new Date(joinDate);
  const formattedJoinDate = isValid(profileJoinDate)
    ? format(profileJoinDate, 'MM/dd/yyyy')
    : null;

  const contentProps = {
    ...props,
    joinDate: formattedJoinDate,
  };

  const hasActiveSubscription = Boolean(activeSubscription);
  const isSweetpass =
    (tier?.name === TierName.SweetpassPlus && !hasActiveSubscription) ||
    tier?.name === TierName.Sweetpass;
  const isSweetpassPlus =
    hasActiveSubscription && tier?.name === TierName.SweetpassPlus;

  const isDowngradedSweetpass = isSweetpass && hasPaidTierHistory;
  const isCancelledSweetpassPlus =
    isSweetpassPlus && tierStatus === TierStatusName.CancellationPending;

  if (isCancelledSweetpassPlus) {
    return getMembershipLevelSectionContentForSweetpassPlusCanceled(
      contentProps,
    );
  }

  if (isSweetpassPlus) {
    return getMembershipLevelSectionContentForSweetpassPlusActive(contentProps);
  }

  if (isDowngradedSweetpass) {
    return getMembershipLevelSectionContentForSweetpassDowngraded(contentProps);
  }

  if (isSweetpass) {
    return getMembershipLevelSectionContentForSweetpassInitial(contentProps);
  }
};

// ––– Tier: Sweetpass (Initial) –––

const getMembershipLevelSectionContentForSweetpassInitial = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataBodySweetpass,
    membershipDataCtaSweetpass,
    joinDate,
    subscribeHandler,
  } = props;

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replace(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS,
    ),
    bodyText: joinDate
      ? membershipDataBodySweetpass.replace('{joinDate}', joinDate)
      : '',
    cta: {
      palette: 'limeOnGreen',
      title: membershipDataCtaSweetpass,
      accessibilityLabel: membershipDataCtaSweetpass,
      handler: subscribeHandler,
    },
  };
};

// ––– Tier: Sweetpass (Downgraded) –––

const getMembershipLevelSectionContentForSweetpassDowngraded = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataCtaSweetpassDowngraded,
    subscribeHandler,
  } = props;

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replace(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS,
    ),
    // if there is ever a requirement for a sweetpass downgraded body text, use this.
    // bodyText: membershipDataBodySweetpassDowngraded,
    cta: {
      palette: 'secondary',
      title: membershipDataCtaSweetpassDowngraded,
      accessibilityLabel: membershipDataCtaSweetpassDowngraded,
      handler: subscribeHandler,
    },
  };
};

// ––– Tier: Sweetpass+ (Active) –––

const getMembershipLevelSectionContentForSweetpassPlusActive = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataBodySweetpassPlus,
    activeSubscription,
  } = props;

  if (!activeSubscription) return undefined;

  const { nextBillingDate, nextBillingPeriodAmount } = activeSubscription;
  const nextBillingPeriodRecurrence = 'mo';

  const formattedNextBillingDate = isValid(new Date(nextBillingDate))
    ? format(new Date(nextBillingDate), 'MM/dd/yyyy')
    : null;

  if (!formattedNextBillingDate) return undefined;

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replace(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS_PLUS,
    ),
    bodyText: membershipDataBodySweetpassPlus
      .replace('{nextBillingDate}', formattedNextBillingDate)
      .replace('{nextBillingPeriodAmount}', nextBillingPeriodAmount)
      .replace('{nextBillingPeriodRecurrence}', nextBillingPeriodRecurrence),
  };
};

// ––– Tier: Sweetpass+ (Canceled) –––

const getMembershipLevelSectionContentForSweetpassPlusCanceled = (
  props: GetMembershipLevelSectionContentProps,
): SweetpassMembershipLevelSectionProps | undefined => {
  const {
    name,
    membershipDataTitle,
    membershipDataHeader,
    membershipDataBodySweetpassPlusCancelled,
    membershipDataCtaSweetpassPlusCancelled,
    activeSubscription,
    reactivateSubscriptionHandler,
  } = props;

  if (!activeSubscription) return undefined;

  const { paidThroughDate } = activeSubscription;

  const paidThroughDateFormatted = paidThroughDate
    ? format(new Date(paidThroughDate), 'MM/dd/yyyy')
    : null;

  return {
    name,
    title: membershipDataTitle,
    headingText: membershipDataHeader.replace(
      '{membershipLevel}',
      MEMBERSHIP_SWEETPASS,
    ),
    bodyText: paidThroughDateFormatted
      ? membershipDataBodySweetpassPlusCancelled.replace(
          '{paidThroughDate}',
          paidThroughDateFormatted,
        )
      : undefined,
    cta: {
      palette: 'secondary',
      title: membershipDataCtaSweetpassPlusCancelled,
      accessibilityLabel: membershipDataCtaSweetpassPlusCancelled,
      handler: reactivateSubscriptionHandler,
    },
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MEMBERSHIP_SWEETPASS = 'Sweetpass';
const MEMBERSHIP_SWEETPASS_PLUS = 'Sweetpass+';

// ─── Types ───────────────────────────────────────────────────────────────────

type SweetpassMembershipLevelCta = AsyncCta;

type GetMembershipLevelSectionProps = {
  readonly autoRenewInfo?: string;
  readonly subscribeHandler: () => Promise<void>;
  readonly reactivateSubscriptionHandler: () => Promise<void>;
} & AccountSweetpassMembershipSection &
  GetSectionProps;

type GetMembershipLevelSectionContentProps = GetMembershipLevelSectionProps &
  AccountSweetpassMembershipSection & {
    readonly joinDate: string | null;
  };

export type SweetpassMembershipLevelSectionProps = {
  readonly name: string;
  readonly title: string;
  readonly headingText?: string;
  readonly bodyText?: string;
  readonly cta?: SweetpassMembershipLevelCta;
} & SweetpassSectionProps;
